// Section-level visibility for the Inside D portal.
//
// This is NOT the same thing as the per-item audience on the form below it:
//
//   per-item audience  - who can see THIS update / policy
//   section visibility - whether the whole section (its nav entry and its page)
//                        exists for a logged-out visitor at all
//
// Both matter. An "Employee only" section is hidden outright, so a visitor
// never sees an empty Policies page and never learns the section exists.
// A section left on "Everyone" still shows each visitor only the items whose
// own audience is General - the item filter is unchanged either way.

import React, { useEffect, useState } from "react";
import { FaGlobe, FaLock } from "react-icons/fa";
import { dnAlert } from "./dNews_Alert";
import { apiFetch } from "./api";
import "./Dolluzcorp_SectionVisibility.css";

const OPTIONS = [
    { value: "everyone", label: "Everyone", icon: FaGlobe, hint: "Visitors and employees can reach this section." },
    { value: "employees", label: "Employee only", icon: FaLock, hint: "Hidden from visitors entirely - the section does not appear for them." },
];

export default function SectionVisibility({ section, label }) {
    const [value, setValue] = useState(null);   // null while loading
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await apiFetch(`/api/Dolluzcorp/sections/visibility?section=${encodeURIComponent(section)}`);
                const d = await r.json();
                if (alive) setValue(d?.visibility === "employees" ? "employees" : "everyone");
            } catch {
                if (alive) setValue("everyone");
            }
        })();
        return () => { alive = false; };
    }, [section]);

    const choose = async (next) => {
        if (next === value || saving) return;
        const previous = value;
        setValue(next);            // optimistic - the control is a toggle, it should feel instant
        setSaving(true);
        try {
            const r = await apiFetch("/api/Dolluzcorp/sections/visibility", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ section, visibility: next }),
            });
            const d = await r.json();
            if (!d.success) throw new Error(d.message || "Save failed");
            dnAlert.success(
                "Visibility updated",
                next === "employees"
                    ? `${label} is now hidden from visitors on the portal.`
                    : `${label} is now visible to everyone on the portal.`
            );
        } catch (e) {
            setValue(previous);    // put it back - the toggle must not lie
            if (e?.devError) console.error("section visibility:", e.devError);
            dnAlert.error("Couldn’t save", "Something went wrong. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="dsv-bar">
            <div className="dsv-text">
                <span className="dsv-label">Visible to</span>
                <span className="dsv-sub">
                    {value === "employees"
                        ? `${label} is hidden from visitors on the portal.`
                        : `Anyone can reach ${label} on the portal.`}
                </span>
            </div>

            <div className="dsv-seg">
                {OPTIONS.map(o => (
                    <button
                        key={o.value}
                        type="button"
                        title={o.hint}
                        disabled={value === null || saving}
                        className={`dsv-opt ${value === o.value ? `active ${o.value}` : ""}`}
                        onClick={() => choose(o.value)}
                    >
                        <o.icon size={11} /> {o.label}
                    </button>
                ))}
            </div>
        </div>
    );
}
